
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BriefcaseIcon, Users, Target, Map, Stamp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AuthButton } from "@/components/auth/AuthButton";
import { useRole } from "@/hooks/useRole";

import { NavLogo } from "@/components/brand/LogoExports";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { HeaderPointsDisplay } from "@/components/gamification/HeaderPointsDisplay";
import { usePoints } from "@/hooks/usePoints";
import { DesktopAvatarMenu } from "./DesktopAvatarMenu";

export function DesktopNavBar() {
  const { user } = useAuth();
  const { role } = useRole();
  const { points, level, loading: pointsLoading } = usePoints();
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  // Points already shown in StatusStrip on the dashboard — hide the nav chip there to avoid duplication.
  const isDashboard = location.pathname === "/dashboard" || location.pathname === "/";

  return (
    <nav aria-label="Main navigation" className="hidden md:flex fixed top-3 left-0 right-0 z-50 px-4 lg:px-10 pointer-events-none text-foreground">
      <div className="gd-dock pointer-events-auto mx-auto w-full max-w-[1480px] h-14 rounded-full pl-5 pr-2">
        <div className="flex h-full items-center gap-4 overflow-hidden">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Link 
              to="/dashboard" 
              className="hover:opacity-80 transition-opacity flex-shrink-0"
            >
              <NavLogo />
            </Link>

            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto overflow-y-hidden flex-nowrap whitespace-nowrap">
              <Link to="/goals">
                <Button variant="ghost" size="sm" className={`flex items-center gap-2 rounded-full h-9 px-4 font-semibold ${isActive('/goals') ? 'bg-secondary text-secondary-foreground shadow-sm' : ''}`}>
                  <Target className="h-4 w-4" />
                  My Goals
                </Button>
              </Link>

              <Link to="/assign">
                <Button variant="ghost" size="sm" className={`flex items-center gap-2 rounded-full h-9 px-4 font-semibold ${location.pathname.startsWith('/assign') || location.pathname.startsWith('/connections') ? 'bg-secondary text-secondary-foreground shadow-sm' : ''}`}>
                  <Users className="h-4 w-4" />
                  Family & Friends
                </Button>
              </Link>

              <Link to="/explore">
                <Button variant="ghost" size="sm" className={`flex items-center gap-2 rounded-full h-9 px-4 font-semibold ${isActive('/explore') ? 'bg-secondary text-secondary-foreground shadow-sm' : ''}`}>
                  <Map className="h-4 w-4" />
                  Explore UAE
                </Button>
              </Link>

              <Link to="/passports">
                <Button variant="ghost" size="sm" className={`flex items-center gap-2 rounded-full h-9 px-4 font-semibold ${location.pathname.startsWith('/passports') || location.pathname.startsWith('/common-goals') ? 'bg-secondary text-secondary-foreground shadow-sm' : ''}`}>
                  <Stamp className="h-4 w-4" />
                  Passports
                </Button>
              </Link>


              {role === 'advertiser' && (
                <Link to="/advertiser-dashboard">
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <BriefcaseIcon className="h-4 w-4" />
                    Advertiser Dashboard
                  </Button>
                </Link>
              )}
              
              {role === 'admin' && (
                <Link to="/admin/partner-analytics">
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <BriefcaseIcon className="h-4 w-4" />
                    Admin Panel
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Points Display — hidden on dashboard (shown in StatusStrip) */}
            {user && !pointsLoading && !isDashboard && (
              <HeaderPointsDisplay
                level={level}
                points={points}
              />

            )}

            {/* Notifications */}
            {user && <NotificationCenter />}

            {/* Avatar dropdown — hosts Profile, Plan status, Rewards, Sign out */}
            {user && <DesktopAvatarMenu />}

            <ThemeToggle />
            <AuthButton />
          </div>
        </div>
      </div>
    </nav>
  );
}

